// Types pour les seuils d'alertes personnalisables
// Ces types définissent la structure des seuils qui déclenchent les alertes dans l'application

export interface SeuilsAlertes {
  // Rentabilité
  marge_critique_pct: number;        // Défaut: 0 - Alerte rouge si marge < X%
  marge_faible_pct: number;          // Défaut: 18 - Alerte orange si marge < X%

  // Délais / Jours restants rentables
  jours_rentables_critique: number;  // Défaut: 2 - Alerte rouge si J-X avant déficit
  jours_rentables_attention: number; // Défaut: 7 - Alerte orange si J-X avant déficit

  // Fin de chantier
  fin_chantier_proche_jours: number; // Défaut: 7 - Alerte X jours avant la fin prévue

  // Budget global
  budget_attention_pct: number;      // Défaut: 80 - Alerte orange si X% du budget consommé
  budget_critique_pct: number;       // Défaut: 100 - Alerte rouge si X% du budget dépassé

  // Budget par catégorie (ventilation)
  budget_categorie_attention_pct: number; // Défaut: 90 - Alerte orange par catégorie
  budget_categorie_critique_pct: number;  // Défaut: 100 - Alerte rouge par catégorie dépassée

  // Factures clients
  facture_rappel_jours: number;      // Défaut: 7 - Alerte orange X jours avant échéance
  facture_retard_jours: number;      // Défaut: 0 - Alerte rouge dès X jours de retard (0 = dès J+1)

  // Divers
  devis_attente_jours: number;       // Défaut: 7 - Alerte si devis en attente > X jours
  suspension_longue_jours: number;   // Défaut: 7 - Alerte si chantier suspendu > X jours
  chantier_sans_equipe_jours: number; // Défaut: 3 - Alerte si chantier démarre dans X jours sans équipe
}

// Valeurs par défaut optimisées pour le BTP
export const DEFAULT_SEUILS: SeuilsAlertes = {
  marge_critique_pct: 0,
  marge_faible_pct: 18,
  jours_rentables_critique: 2,
  jours_rentables_attention: 7,
  fin_chantier_proche_jours: 1,
  budget_attention_pct: 90,           // CHANGÉ : 80 → 90 (réduire alertes prématurées)
  budget_critique_pct: 100,
  budget_categorie_attention_pct: 90,
  budget_categorie_critique_pct: 100,
  facture_rappel_jours: 3,            // CHANGÉ : 7 → 3 (relance plus proche échéance)
  facture_retard_jours: 0,
  devis_attente_jours: 14,            // CHANGÉ : 7 → 14 (client prend son temps)
  suspension_longue_jours: 14,        // CHANGÉ : 7 → 14 (suspensions courantes en BTP)
  chantier_sans_equipe_jours: 3,
};

// Configuration pour l'UI du formulaire de paramétrage
// Organise les seuils par catégories avec labels et descriptions

interface SeuilField {
  key: keyof SeuilsAlertes;
  label: string;
  unit: string;
  hint: string;
  min?: number;
  max?: number;
}

interface SeuilCategory {
  label: string;
  fields: SeuilField[];
}

export const SEUILS_CONFIG: Record<string, SeuilCategory> = {
  rentabilite: {
    label: '📊 Rentabilité',
    fields: [
      {
        key: 'marge_critique_pct',
        label: 'Marge critique (alerte rouge)',
        unit: '%',
        hint: 'Défaut: 0% (= déficit)',
        min: -50,
        max: 50,
      },
      {
        key: 'marge_faible_pct',
        label: 'Marge faible (alerte orange)',
        unit: '%',
        hint: 'Défaut: 18% (seuil BTP recommandé)',
        min: 0,
        max: 50,
      },
    ],
  },
  delais: {
    label: '⏱️ Délais',
    fields: [
      {
        key: 'jours_rentables_critique',
        label: 'Jours rentables critique (alerte rouge)',
        unit: 'jours',
        hint: 'Défaut: 2 jours avant passage en déficit',
        min: 0,
        max: 30,
      },
      {
        key: 'jours_rentables_attention',
        label: 'Jours rentables attention (alerte orange)',
        unit: 'jours',
        hint: 'Défaut: 7 jours avant passage en déficit',
        min: 0,
        max: 60,
      },
      {
        key: 'fin_chantier_proche_jours',
        label: 'Fin de chantier proche',
        unit: 'jours',
        hint: 'Alerte X jours avant la date de fin prévue',
        min: 1,
        max: 30,
      },
    ],
  },
  budget: {
    label: '💰 Budget',
    fields: [
      {
        key: 'budget_attention_pct',
        label: 'Budget global attention (alerte orange)',
        unit: '%',
        hint: 'Alerte quand X% du budget total est consommé',
        min: 50,
        max: 100,
      },
      {
        key: 'budget_critique_pct',
        label: 'Budget global dépassé (alerte rouge)',
        unit: '%',
        hint: 'Défaut: 100% (alerte dès dépassement)',
        min: 80,
        max: 150,
      },
      {
        key: 'budget_categorie_attention_pct',
        label: 'Budget catégorie attention (alerte orange)',
        unit: '%',
        hint: 'Alerte par catégorie (main d\'oeuvre, matériaux, etc.)',
        min: 50,
        max: 100,
      },
      {
        key: 'budget_categorie_critique_pct',
        label: 'Budget catégorie dépassé (alerte rouge)',
        unit: '%',
        hint: 'Alerte rouge si une catégorie dépasse son budget',
        min: 80,
        max: 150,
      },
    ],
  },
  factures: {
    label: '📄 Factures clients',
    fields: [
      {
        key: 'facture_rappel_jours',
        label: 'Rappel avant échéance',
        unit: 'jours',
        hint: 'Alerte X jours avant la date d\'échéance',
        min: 1,
        max: 30,
      },
      {
        key: 'facture_retard_jours',
        label: 'Retard de paiement (alerte rouge)',
        unit: 'jours',
        hint: '0 = alerte dès le 1er jour de retard',
        min: 0,
        max: 30,
      },
    ],
  },
  divers: {
    label: '📋 Divers',
    fields: [
      {
        key: 'devis_attente_jours',
        label: 'Devis en attente',
        unit: 'jours',
        hint: 'Alerte si devis envoyé depuis X jours sans réponse',
        min: 1,
        max: 60,
      },
      {
        key: 'suspension_longue_jours',
        label: 'Suspension longue',
        unit: 'jours',
        hint: 'Alerte si chantier suspendu depuis X jours',
        min: 1,
        max: 60,
      },
      {
        key: 'chantier_sans_equipe_jours',
        label: 'Chantier sans équipe',
        unit: 'jours',
        hint: 'Alerte si chantier démarre dans X jours sans équipe assignée',
        min: 1,
        max: 14,
      },
    ],
  },
};
